/**
 * AVM Public QR Shop Card — no-auth landing at /avm/:slug
 * Scan → opens this page → auto-opens WhatsApp with intro OR fills onboarding form
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AVMPublicCard() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const forceOnboard = searchParams.get('onboard') === '1';
  const [card, setCard] = useState(null);
  const [waUrl, setWaUrl] = useState('');
  const [err, setErr] = useState(null);
  const [onboardMode, setOnboardMode] = useState(forceOnboard);
  const [onboardData, setOnboardData] = useState({ name: '', mobile: '', whatsapp: '', dob: '', anniversary: '', event_date: '', category: '', special_requirement: '', family_members: [] });
  const [onboardBusy, setOnboardBusy] = useState(false);
  const [onboardDone, setOnboardDone] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/api/avm/public/${slug}`).then((r) => {
      setCard(r.data.card); setWaUrl(r.data.whatsapp_url);
    }).catch((e) => setErr(e?.response?.data?.detail || 'Shop card not available'));
  }, [slug]);

  const openWa = () => {
    axios.post(`${BASE}/api/avm/public/${slug}/click`).catch(() => {});
    window.location.href = waUrl;
  };

  const saveContact = () => {
    if (!card) return;
    const digits = String(card.whatsapp_number || '').replace(/[^\d]/g, '');
    const tel = digits.length === 10 ? `+91${digits}` : `+${digits}`;
    const org = card.shop_name || 'Shop';
    const fn = card.owner_name || card.shop_name || 'Shop';
    const note = (card.intro_message || '').replace(/[\r\n,;]/g, ' ').slice(0, 200);
    const vcf = [
      'BEGIN:VCARD', 'VERSION:3.0',
      `FN:${fn}`, `ORG:${org}`,
      `TEL;TYPE=CELL,VOICE:${tel}`, `TEL;TYPE=WHATSAPP:${tel}`,
      `NOTE:${note}`, `URL:${window.location.href}`,
      'END:VCARD',
    ].join('\n');
    const blob = new Blob([vcf], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(org || 'shop').replace(/[^a-z0-9]+/gi, '_')}.vcf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    axios.post(`${BASE}/api/avm/public/${slug}/click`).catch(() => {});
  };

  const submitOnboard = async () => {
    if (!onboardData.name && !onboardData.mobile && !onboardData.whatsapp) {
      alert('Kripya name/mobile/WhatsApp me se koi ek bharein'); return;
    }
    setOnboardBusy(true);
    try {
      await axios.post(`${BASE}/api/avm/public/${slug}/onboard`, onboardData);
      setOnboardDone(true);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    } finally { setOnboardBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-6 text-center" data-testid="avm-public-err">
      <div><div className="text-4xl mb-3">🔒</div><div className="text-lg font-black">{err}</div></div>
    </div>
  );
  if (!card) return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-emerald-300">Loading...</div>;

  const theme = card.theme_color || '#10b981';

  // ONBOARDING MODE
  if (onboardMode) {
    if (onboardDone) return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(160deg, ${theme}, #0f172a)` }} data-testid="avm-onboard-done">
        <div className="max-w-sm w-full bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-8 text-white text-center shadow-2xl">
          <div className="text-6xl mb-3">🎉</div>
          <div className="text-2xl font-black">Dhanyavaad!</div>
          <div className="text-sm opacity-80 mt-2 mb-6">Aapki details {card.shop_name} ke saath save ho gayi. Aapko special offers milte rahenge. 🙏</div>
          <button onClick={openWa} className="w-full py-3 rounded-xl bg-emerald-500 text-slate-900 font-black">💬 Ab WhatsApp par baat karein</button>
        </div>
      </div>
    );
    return (
      <div className="min-h-screen p-4 flex items-center justify-center" style={{ background: `linear-gradient(160deg, ${theme}, #0f172a)` }} data-testid="avm-onboard-form">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-5 text-white shadow-2xl space-y-3">
          <div className="text-center">
            <div className="text-4xl mb-1">📝</div>
            <div className="text-lg font-black">{card.shop_name}</div>
            <div className="text-xs opacity-80">Apni details bharein — humaari taraf se special offers milte rahenge 🎁</div>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <Field label="Aapka Naam" value={onboardData.name} onChange={(v) => setOnboardData({ ...onboardData, name: v })} testid="onboard-name" />
            <Field label="Mobile Number" value={onboardData.mobile} onChange={(v) => setOnboardData({ ...onboardData, mobile: v })} testid="onboard-mobile" />
            <Field label="WhatsApp (agar alag hai)" value={onboardData.whatsapp} onChange={(v) => setOnboardData({ ...onboardData, whatsapp: v })} />
            <div className="grid grid-cols-2 gap-2">
              <DateField label="🎂 Birthday" value={onboardData.dob} onChange={(v) => setOnboardData({ ...onboardData, dob: v })} />
              <DateField label="💐 Anniversary" value={onboardData.anniversary} onChange={(v) => setOnboardData({ ...onboardData, anniversary: v })} />
            </div>
            <DateField label="📅 Important Event Date" value={onboardData.event_date} onChange={(v) => setOnboardData({ ...onboardData, event_date: v })} />
            <Field label="Category (Family / Friend / VIP etc.)" value={onboardData.category} onChange={(v) => setOnboardData({ ...onboardData, category: v })} />
            <FamilyMembersBlock members={onboardData.family_members} onChange={(list) => setOnboardData({ ...onboardData, family_members: list })} />
            <div>
              <label className="text-[10px] opacity-70">Special Requirement (agar koi ho)</label>
              <textarea value={onboardData.special_requirement} onChange={(e) => setOnboardData({ ...onboardData, special_requirement: e.target.value })} rows={2}
                        className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/40" />
            </div>
          </div>
          <div className="text-[10px] text-white/60 text-center">Sab fields optional hain — koi bhi 1 bhar dijiye</div>
          <button onClick={submitOnboard} disabled={onboardBusy} data-testid="avm-onboard-submit"
                  className="w-full py-3 rounded-xl bg-emerald-500 text-slate-900 font-black disabled:opacity-50">
            {onboardBusy ? 'Saving...' : '💾 Submit Details'}
          </button>
          <button onClick={() => setOnboardMode(false)} className="w-full py-2 text-xs opacity-70 underline">Skip · Just Open WhatsApp</button>
        </div>
      </div>
    );
  }

  // DEFAULT SHOP CARD MODE
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(160deg, ${theme}, #0f172a)` }} data-testid="avm-public-card">
      <div className="max-w-sm w-full bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6 text-white text-center shadow-2xl">
        <div className="text-5xl mb-3">🏪</div>
        <div className="text-2xl font-black">{card.shop_name}</div>
        {card.owner_name && <div className="text-sm opacity-80 mt-1">{card.owner_name}</div>}
        <div className="text-xs opacity-70 mt-3 mb-4">Scan karke aap yahaan pahunche 👇</div>
        <button onClick={openWa} data-testid="avm-public-open-wa"
                className="w-full py-3 rounded-xl bg-emerald-500 text-slate-900 font-black text-lg hover:scale-[1.02] transition">
          💬 Open WhatsApp
        </button>
        <button onClick={saveContact} data-testid="avm-public-save-contact"
                className="w-full mt-2 py-2.5 rounded-xl bg-white/15 border-2 border-white/25 text-white font-black text-sm hover:bg-white/25 transition">
          📇 Save to Phone Contacts
        </button>
        <button onClick={() => setOnboardMode(true)} data-testid="avm-public-onboard-btn"
                className="w-full mt-2 py-2.5 rounded-xl bg-cyan-500/30 border-2 border-cyan-300 text-white font-black text-sm hover:bg-cyan-500/50 transition">
          📝 Fill My Details (Get Offers)
        </button>
        <div className="text-[10px] opacity-60 mt-4">Powered by Anant Web Portal · 0-cost Vyapaar Master</div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] opacity-70">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/40" />
    </div>
  );
}
function DateField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[10px] opacity-70">{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)}
             className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm" />
    </div>
  );
}

function FamilyMembersBlock({ members, onChange }) {
  const RELATIONS = ['spouse', 'son', 'daughter', 'father', 'mother', 'sibling', 'other'];
  const add = () => onChange([...(members || []), { name: '', relation: 'spouse', dob: '', anniversary: '' }]);
  const upd = (i, patch) => onChange(members.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const del = (i) => onChange(members.filter((_, idx) => idx !== i));
  return (
    <div className="rounded-xl bg-white/5 border border-white/15 p-2 space-y-2" data-testid="onboard-family-block">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-black opacity-80">👨‍👩‍👧 Family 360 · Add family birthdays</div>
        <button type="button" onClick={add} data-testid="onboard-family-add"
                className="text-[10px] px-2 py-0.5 rounded bg-emerald-500 text-slate-900 font-black">+ Add</button>
      </div>
      {(members || []).map((m, i) => (
        <div key={i} className="rounded-lg bg-white/5 border border-white/10 p-2 space-y-1" data-testid={`onboard-family-row-${i}`}>
          <div className="grid grid-cols-2 gap-2">
            <input value={m.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Name" data-testid={`onboard-family-name-${i}`}
                   className="px-2 py-1.5 rounded bg-white/10 border border-white/20 text-white text-xs placeholder:text-white/40" />
            <select value={m.relation} onChange={(e) => upd(i, { relation: e.target.value })} data-testid={`onboard-family-rel-${i}`}
                    className="px-2 py-1.5 rounded bg-white/10 border border-white/20 text-white text-xs">
              {RELATIONS.map((r) => <option key={r} value={r} className="text-slate-900">{r}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="date" value={m.dob || ''} onChange={(e) => upd(i, { dob: e.target.value })} data-testid={`onboard-family-dob-${i}`}
                   placeholder="🎂 DOB" className="px-2 py-1.5 rounded bg-white/10 border border-white/20 text-white text-xs" />
            <input type="date" value={m.anniversary || ''} onChange={(e) => upd(i, { anniversary: e.target.value })}
                   placeholder="💐 Anniversary" className="px-2 py-1.5 rounded bg-white/10 border border-white/20 text-white text-xs" />
          </div>
          <button type="button" onClick={() => del(i)} className="text-[10px] text-rose-300 underline">✕ Remove</button>
        </div>
      ))}
      {(members || []).length === 0 && (
        <div className="text-[10px] opacity-60 text-center">Family members ke naam + birthday add karein · Auto-wish milega</div>
      )}
    </div>
  );
}
